

import { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { X, TrendingUp, Calendar, DollarSign, Download } from "lucide-react";
import { format, parseISO } from "date-fns";
import { RosterEntry, Game } from "@/types";
import { getAuth } from "firebase/auth";
import { getFirebaseServices } from "@/lib/firebase/client";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { normalizeRef } from "@/lib/cpi";
import { EarningsChart } from "./EarningsChart";
import CopyButton from "@/components/CopyButton";
import { useMultipleVideoMetadata } from "@/hooks/useVideoMetadata";

interface Props {
  entry: RosterEntry | null;
  game: Game | null;
  open: boolean;
  onClose: () => void;
}

interface InstallEvent {
  date: string;
  // firestore Timestamp or ISO string or number
  timestamp?: any;
  cpi: number;
  eventCount: number;
}

interface TimelineDay {
  date: string;
  installs: number;
  earnings: number;
}

export function GameEarningsSheet({ entry, game, open, onClose }: Props) {
  const [timeline, setTimeline] = useState<TimelineDay[]>([]);
  const [events, setEvents] = useState<InstallEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch video metadata
  const { metadataList, loading: metadataLoading } = useMultipleVideoMetadata(
    game?.gameplay || []
  );

  useEffect(() => {
    if (!entry || !game || !open) return;

    const loadEvents = () => {
      setLoading(true);
      console.log('[GameEarningsSheet] Loading events for game:', game?.slug, 'game.name:', game?.name);
      
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      console.log('[GameEarningsSheet] auth.currentUser uid:', auth.currentUser?.uid, 'emailVerified:', auth.currentUser?.emailVerified);
      if (!userId) {
        setLoading(false);
        return;
      }

      const services = getFirebaseServices();
      if (!services) {
        setLoading(false);
        return;
      }

      const { db } = services;
      const eventsRef = collection(db, `users/${userId}/installEvents`);
      const normGame = normalizeRef(game.slug);

      // Try to extract the `ref` query param from the affiliate link (affiliateLink
      // is stored on the roster entry). The `ref` param is the normalized handle
      // used when syncing GA4 events (see buildAffiliateLink). If present, filter
      // install events by both `game` and `ref` so we only show installs for the
      // selected social account's affiliate link.
      let normRefForQuery: string | null = null;
      try {
        const url = new URL(entry.affiliateLink);
        const refParam = url.searchParams.get('ref') || url.searchParams.get('campaign_ref') || '';
        if (refParam) normRefForQuery = normalizeRef(refParam);
      } catch (e) {
        // affiliateLink may not be a full URL; attempt to extract a raw handle
        const raw = String(entry.affiliateLink || '').split('?')[1] || '';
        const m = raw.match(/ref=([^&]+)/);
        if (m && m[1]) normRefForQuery = normalizeRef(decodeURIComponent(m[1]));
      }

      // Build query: filter by game and order by timestamp. We avoid adding an
      // equality filter on `ref` here to prevent requiring a composite index.
      // Instead, if a ref filter is present, we'll filter client-side after
      // receiving the snapshot. This keeps queries index-friendly while still
      // scoping analytics to a single affiliate ref.
      const q = query(eventsRef, where('game', '==', normGame), orderBy('timestamp', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allEvents = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            date: data.date || '',
            timestamp: data.timestamp,
            cpi: data.cpi || 0,
            eventCount: data.eventCount || 0,
            ref: data.ref || ''
          } as InstallEvent & { ref?: string };
        });

        console.log('[GameEarningsSheet] Fetched events count (before ref filter):', allEvents.length, 'normRefForQuery:', normRefForQuery);

        const fetchedEvents = normRefForQuery
          ? allEvents.filter(ev => ((ev.ref || '') && normalizeRef(ev.ref || '')) === normRefForQuery)
          : allEvents;

        console.log('[GameEarningsSheet] Events after ref filter:', fetchedEvents.length);
        setEvents(fetchedEvents);

        // Helper to parse timestamp from Firestore Timestamp or string
        function parseEventDate(e: InstallEvent): Date | null {
          if (!e) return null;
          // Firestore client Timestamp
          // @ts-ignore
          if (e.timestamp && typeof e.timestamp.toDate === 'function') {
            // @ts-ignore
            return e.timestamp.toDate();
          }
          if (typeof e.timestamp === 'number') return new Date(e.timestamp);
          if (typeof e.timestamp === 'string' && e.timestamp.length > 0) {
            try { return parseISO(e.timestamp); } catch { /* noop */ }
          }

          // If date contains a trailing -HHMM (time in EST, GMT-5), e.g. "blasthole3d-and-joellmp4-1634"
          if (e.date && typeof e.date === 'string') {
            const m = e.date.match(/-(\d{3,4})$/);
            if (m) {
              const hm = m[1];
              // parse hours and minutes
              const h = hm.length === 3 ? parseInt(hm.slice(0,1), 10) : parseInt(hm.slice(0,2), 10);
              const min = hm.length === 3 ? parseInt(hm.slice(1), 10) : parseInt(hm.slice(2), 10);

              // Determine base date: prefer event.timestamp date if available, else use current date in EST
              let baseYear: number, baseMonth: number, baseDay: number;
              // try stored timestamp if present as string on e.timestamp
              try {
                if (e.timestamp && typeof (e.timestamp as any).toDate === 'function') {
                  const d = (e.timestamp as any).toDate();
                  baseYear = d.getFullYear(); baseMonth = d.getMonth() + 1; baseDay = d.getDate();
                } else if (typeof e.timestamp === 'number') {
                  const d = new Date(e.timestamp);
                  baseYear = d.getFullYear(); baseMonth = d.getMonth() + 1; baseDay = d.getDate();
                } else {
                  // current date in EST (UTC-5)
                  const now = new Date();
                  const estNow = new Date(now.getTime() - 5 * 60 * 60 * 1000);
                  baseYear = estNow.getFullYear(); baseMonth = estNow.getMonth() + 1; baseDay = estNow.getDate();
                }
              } catch (err) {
                const now = new Date();
                const estNow = new Date(now.getTime() - 5 * 60 * 60 * 1000);
                baseYear = estNow.getFullYear(); baseMonth = estNow.getMonth() + 1; baseDay = estNow.getDate();
              }

              const mm = String(baseMonth).padStart(2, '0');
              const dd = String(baseDay).padStart(2, '0');
              const hh = String(h).padStart(2, '0');
              const mins = String(min).padStart(2, '0');

              // Construct an ISO string with -05:00 offset (EST) so JS will convert to local time
              const iso = `${baseYear}-${mm}-${dd}T${hh}:${mins}:00-05:00`;
              const parsed = new Date(iso);
              if (!isNaN(parsed.getTime())) return parsed;
            }

            // fallback to YYYYMMDD
            if (e.date.length === 8) {
              try { return parseISO(e.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')); } catch { }
            }
          }
          return null;
        }

        // Group events by day using parsed timestamp
        const byDate = fetchedEvents.reduce((acc, event) => {
          const d = parseEventDate(event);
          const dateKey = d ? format(d, 'yyyyMMdd') : (event.date || 'unknown');
          if (!acc[dateKey]) acc[dateKey] = { date: dateKey, installs: 0, earnings: 0 };
          acc[dateKey].installs += event.eventCount;
          acc[dateKey].earnings += event.cpi * event.eventCount;
          return acc;
        }, {} as Record<string, TimelineDay>);

        const timelineData = Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
        const totalFromTimeline = timelineData.reduce((sum, day) => sum + day.earnings, 0);
        console.log('[GameEarningsSheet] Timeline computed:', { 
          game: game?.slug, 
          timelineDays: timelineData.length, 
          totalFromTimeline: totalFromTimeline.toFixed(4),
          eventsCount: fetchedEvents.length 
        });
        // Debug: log parsed timestamps for each fetched event
        // Sample events for debugging
        if (fetchedEvents.length > 0 && fetchedEvents.length <= 5) {
          console.log('[GameEarningsSheet] Sample events:', fetchedEvents.map(ev => ({
            // @ts-ignore
            id: ev.id,
            date: ev.date,
            // @ts-ignore
            timestamp: ev.timestamp && typeof ev.timestamp.toDate === 'function' ? ev.timestamp.toDate().toISOString() : ev.timestamp,
            cpi: ev.cpi,
            eventCount: ev.eventCount,
            earnings: (ev.cpi * ev.eventCount).toFixed(4)
          })));
        }
        setTimeline(timelineData);
        setLoading(false);
      }, (error) => {
        console.error('Failed to load install events:', error);
        setLoading(false);
        // Surface a friendly error for permission issues and avoid uncaught errors
        // in the console that can be confusing to the user.
        if (error && (error as any).code === 'permission-denied') {
          setError('You do not have permission to view these install events. Make sure you are signed in with the correct account.');
        } else {
          setError('Failed to load install events.');
        }
      });

      return unsubscribe;
    };

    const cleanup = loadEvents();
    return () => {
      if (cleanup) cleanup();
    };
  }, [entry, game, open]);

  if (!entry || !game) return null;

  // Compute totals from events array (source of truth)
  const totalInstalls = events.reduce((sum, e) => sum + e.eventCount, 0);
  const totalEarnings = events.reduce((sum, e) => sum + (e.cpi * e.eventCount), 0);
  const avgCpi = totalInstalls > 0 ? totalEarnings / totalInstalls : 0;
  const daysActive = Math.floor(
    (new Date().getTime() - new Date(entry.startedAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  // Helpers to parse/format durations that may come from Firestore as strings
  // (e.g. "7:41") or as numbers (seconds).
  const parseDurationString = (s: string | number): number => {
    if (typeof s === 'number') return Math.floor(s);
    const parts = String(s).split(':').map((p) => parseInt(p, 10));
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + (parts[1] || 0);
    return Number(s) || 0;
  };

  const formatDurationDisplay = (v: number | string | undefined): string => {
    if (typeof v === 'string') return v;
    if (typeof v === 'number' && !Number.isNaN(v)) {
      const total = Math.floor(v);
      const mm = Math.floor(total / 60);
      const ss = total % 60;
      return `${mm}:${String(ss).padStart(2, '0')}`;
    }
    return metadataLoading ? 'Loading...' : 'Unknown';
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/90" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative mx-auto w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-zinc-950 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 transition hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <Dialog.Title className="mb-4 text-2xl font-semibold">
            {game.name}
          </Dialog.Title>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
              <div className="flex items-center gap-2 text-xs text-emerald-200">
                <DollarSign className="h-4 w-4" />
                Total Earnings
              </div>
              <p className="mt-1 text-2xl font-bold text-emerald-300">
                ${totalEarnings.toFixed(2)}
              </p>
            </div>

            <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 p-4">
              <div className="flex items-center gap-2 text-xs text-blue-200">
                <TrendingUp className="h-4 w-4" />
                Total Installs
              </div>
              <p className="mt-1 text-2xl font-bold text-blue-300">
                {totalInstalls}
              </p>
            </div>

            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4">
              <div className="flex items-center gap-2 text-xs text-amber-200">
                <DollarSign className="h-4 w-4" />
                Avg CPI
              </div>
              <p className="mt-1 text-2xl font-bold text-amber-300">
                ${avgCpi.toFixed(2)}
              </p>
            </div>

            <div className="rounded-2xl border border-purple-500/20 bg-purple-500/10 p-4">
              <div className="flex items-center gap-2 text-xs text-purple-200">
                <Calendar className="h-4 w-4" />
                Days Active
              </div>
              <p className="mt-1 text-2xl font-bold text-purple-300">
                {daysActive}
              </p>
            </div>
          </div>

          {/* Analytics Chart */}
          <div className="mb-6">
            {loading ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-12 text-center text-sm text-white/60">
                Loading analytics...
              </div>
            ) : (
              <EarningsChart events={events} />
            )}
          </div>

          <div className="mb-4">
            <h3 className="mb-3 text-lg font-semibold">Earnings Timeline</h3>
            {loading ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center text-sm text-white/60">
                Loading timeline...
              </div>
            ) : timeline.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center text-sm text-white/60">
                No install events yet. Share your affiliate link to start earning!
              </div>
            ) : (
              <div className="space-y-2">
                {timeline.slice(0, 10).map((day) => (
                  <div
                    key={day.date}
                    className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">
                        {format(parseISO(day.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')), 'MMM d, yyyy')}
                      </p>
                      <p className="text-xs text-white/60">{day.installs} installs</p>
                    </div>
                    <p className="text-lg font-semibold text-emerald-300">
                      ${day.earnings.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <h3 className="mb-3 text-sm font-semibold text-white/80">Campaign Details</h3>

            <div className="mb-3 flex items-center justify-between">
              <div className="space-y-1 text-sm text-white/60">
                <div className="flex items-center gap-3">
                  <div className={`rounded-full px-3 py-1 text-xs font-semibold ${entry.status === 'archived' ? 'bg-red-600/20 text-red-200 ring-2 ring-red-600/10' : 'bg-emerald-600/20 text-emerald-200 ring-2 ring-emerald-400/10'}`}>
                    {entry.status === 'archived' ? 'Archived' : 'Active'}
                  </div>
                  <div className="text-xs text-white/60">Started: {format(new Date(entry.startedAt), "MMM d, yyyy")}</div>
                </div>
                <div className="text-sm mt-3">
                  <div className="text-xs text-white/60 mb-1">Affiliate Link</div>
                  <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/40 p-3">
                    <span className="grow break-all text-xs text-white">{entry.affiliateLink}</span>
                    <CopyButton text={entry.affiliateLink} />
                  </div>
                </div>
              </div>
            </div>

            {/* Gameplay footage */}
            <div className="mt-4">
              <h3 className="mb-3 text-sm font-semibold text-white/80">Gameplay Videos</h3>
              {!game.gameplay || game.gameplay.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
                  <p className="text-sm text-white/40">Unavailable at this time</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {game.gameplay.slice(0, 3).map((url, idx) => {
                    // Prefer precomputed metadata from Firestore game document if present
                    const preDur = game.videoDuration && game.videoDuration.length > idx ? game.videoDuration[idx] : undefined;
                    // Accept new `videoSize` field; fall back to legacy `videoLength` if present
                    const preSize = (game as any).videoSize && (game as any).videoSize.length > idx
                      ? (game as any).videoSize[idx]
                      : (game as any).videoLength && (game as any).videoLength.length > idx
                      ? (game as any).videoLength[idx]
                      : undefined;

                    const metadata = metadataList[idx];

                    const durationValue = preDur ?? (metadata ? metadata.duration : undefined);
                    const sizeValue = preSize ?? (metadata ? metadata.size : undefined);

                    const duration = formatDurationDisplay(durationValue as number | string | undefined);

                    const size = typeof sizeValue === 'number'
                      ? `${(sizeValue / (1024 * 1024)).toFixed(1)}MB`
                      : metadataLoading
                      ? 'Loading...'
                      : 'Unknown';

                    // Build numeric durations array for comparison (seconds)
                    let durationsForComparison: number[] = [];
                    if (game.videoDuration && game.videoDuration.length > 0) {
                      durationsForComparison = game.videoDuration.map((d) => parseDurationString(d));
                    } else {
                      durationsForComparison = metadataList.filter(Boolean).map((m) => Math.floor(m!.duration));
                    }

                    const longestDuration = durationsForComparison.length > 0 ? Math.max(...durationsForComparison) : 0;
                    const currentDurationSeconds = typeof durationValue === 'number' ? Math.floor(durationValue) : (typeof durationValue === 'string' ? parseDurationString(durationValue) : 0);
                    const isTopPerformer = currentDurationSeconds === longestDuration && longestDuration > 0;
                    
                    return (
                      <div key={url} className="relative group">
                        {/* Video container with 9:16 aspect ratio (vertical) */}
                        <div className="relative w-full" style={{ aspectRatio: '9/16' }}>
                          <video
                            src={url}
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="w-full h-full rounded-2xl border border-white/10 object-cover"
                          />
                          
                          {/* Metadata overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent rounded-2xl pointer-events-none" />
                          
                          {isTopPerformer && (
                            <div className="absolute top-2 right-2 rounded-full bg-lime-400 px-2 py-1 text-xs font-semibold text-black shadow-lg animate-pulse">
                              Top Performer
                            </div>
                          )}
                          
                          {/* Metadata bottom overlay */}
                          <div className="absolute bottom-2 left-2 right-2 text-white text-xs space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">Video {idx + 1}</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/80">
                              <span>{duration}</span>
                              <span>•</span>
                              <span>{size}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Download button */}
                        <a
                          href={url}
                          download
                          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white transition hover:bg-white/10 hover:border-white/20"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download Now
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
